import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const PortfolioLightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  const isOpen = currentIndex !== null && currentIndex >= 0;
  const current = isOpen ? images[currentIndex] : null;

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'Escape') onClose();
    },
    [isOpen, onNext, onPrev, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!current) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-6xl w-[95vw] bg-amber-950 border-amber-800 p-0 overflow-hidden"
        data-testid="portfolio-lightbox"
      >
        <VisuallyHidden.Root>
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription>{current.description}</DialogDescription>
        </VisuallyHidden.Root>
        <div className="relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-20 bg-amber-900/80 hover:bg-amber-800 text-white rounded-full p-2 transition-colors"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Image counter */}
          <div className="absolute top-3 left-3 z-20 bg-amber-900/80 text-white text-sm px-3 py-1 rounded-full">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Prev button */}
          {images.length > 1 && (
            <button
              onClick={onPrev}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 z-20 bg-amber-900/80 hover:bg-amber-800 text-white rounded-full p-3 transition-colors"
              data-testid="lightbox-prev"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next button */}
          {images.length > 1 && (
            <button
              onClick={onNext}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 z-20 bg-amber-900/80 hover:bg-amber-800 text-white rounded-full p-3 transition-colors"
              data-testid="lightbox-next"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Image */}
          <div className="flex items-center justify-center bg-black/50" style={{ minHeight: '60vh' }}>
            <img
              src={current.image}
              alt={current.title}
              className="max-w-full max-h-[75vh] object-contain"
              data-testid="lightbox-image"
            />
          </div>

          {/* Caption */}
          <div className="bg-gradient-to-t from-amber-950 to-amber-900 p-6 text-amber-50">
            <div className="text-sm text-amber-300 font-medium mb-1">{current.category}</div>
            <h3 className="text-2xl font-bold mb-2">{current.title}</h3>
            <p className="text-amber-100">{current.description}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioLightbox;
